import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import type { ComponentType } from "react";
import { ArrowRight } from "lucide-react";

import { PageHero } from "@/components/site/Blocks";
import { ArticleTypeBadge } from "@/components/site/ArticleTypeBadge";
import { ShareLinks } from "@/components/site/ShareLinks";
import { TrServiceLinks } from "@/components/site/TrServiceLinks";
import { ViDAOverviewGraphicTr } from "@/components/site/ViDAOverviewGraphicTr";
import { pageLinks } from "@/lib/locale-alternates";
import { breadcrumb, faqPage, graph, ORG_ID, url, webPage, WEBSITE_ID } from "@/lib/schema";
import { socialMeta } from "@/lib/seo";
import { founderRef } from "@/lib/person-schema";
import { formatTrDate } from "@/lib/tr-date";
import type { TrBlogPost } from "@/lib/tr-blog";
import { ArticleRichText } from "@/components/site/ArticleRichText";

export const Route = createFileRoute("/tr/blog_/$slug")({
  // Articles are stored in the database; the adapter keeps the render shape.
  loader: async ({ params }) => {
    const [{ getPublishedArticle }, { toTrBlogPost, trCategoryName }] = await Promise.all([
      import("@/lib/articles.functions"),
      import("@/lib/tr-blog-db"),
    ]);
    const { article, others } = await getPublishedArticle({
      data: { locale: "tr", slug: params.slug },
    });
    if (!article) throw notFound();
    return {
      post: toTrBlogPost(article),
      categorySlug: article.category ?? "",
      others: others.map(({ slug, title, category }) => ({
        slug,
        title,
        category: trCategoryName(category),
      })),
    };
  },

  head: ({ params, loaderData }) => {
    const post = loaderData?.post;
    if (!post) return {};
    const path = `/tr/blog/${params.slug}`;
    const trail = trailFor(post);

    return {
      meta: [
        { title: post.metaTitle },
        { name: "description", content: post.metaDescription },
        { name: "keywords", content: post.keywords.join(", ") },
        ...socialMeta({
          title: post.metaTitle,
          description: post.metaDescription,
          path,
          locale: "tr_TR",
          type: "article",
          socialTitle: post.title,
          imageAlt: `OXORY blog yazısı: ${post.title}`,
          articleId: post.articleIndex,
        }),
        { property: "article:published_time", content: post.published },
        { property: "article:modified_time", content: post.updated },
      ],
      links: pageLinks(path),
      scripts: [
        graph(
          { "@id": ORG_ID },
          { "@id": WEBSITE_ID },
          webPage({
            path,
            name: post.metaTitle,
            description: post.metaDescription,
            inLanguage: "tr",
          }),
          breadcrumb(trail),
          {
            "@type": "BlogPosting",
            "@id": `${url(path)}#article`,
            headline: post.title,
            description: post.metaDescription,
            inLanguage: "tr",
            datePublished: post.published,
            dateModified: post.updated,
            author: founderRef("tr"),
            publisher: { "@id": ORG_ID },
            keywords: post.keywords.join(", "),
            mainEntityOfPage: { "@id": `${url(path)}#webpage` },
            ...(post.articleIndex ? { identifier: post.articleIndex } : {}),
          },
          ...(import.meta.env.SSR ? [faqPage(post.faq)] : []),
        ),
      ],
    };
  },
  component: TrBlogArticle,
});

function trailFor(post: TrBlogPost) {
  return [
    { name: "Ana Sayfa", path: "/tr" },
    { name: "Blog", path: "/tr/blog" },
    { name: post.title, path: `/tr/blog/${post.slug}` },
  ];
}

/** Slug-specific inline graphics; other posts stay on the generic renderer. */
const TR_ARTICLE_GRAPHICS: Record<string, ComponentType> = {
  "vida-ab-dijital-cagda-kdv": ViDAOverviewGraphicTr,
};

function TrBlogArticle() {
  const { post, categorySlug, others } = Route.useLoaderData();
  const ArticleGraphic = TR_ARTICLE_GRAPHICS[post.slug];


  return (
    <>
      <PageHero eyebrow={post.category} title={post.title} intro={post.excerpt} trail={trailFor(post)} />

      <article className="section-shell py-16 md:py-20">
        <ArticleTypeBadge type="blog" locale="tr" />
        <ShareLinks className="mt-4" path={`/tr/blog/${post.slug}`} title={post.title} locale="tr" />
        <p className="mt-3 text-sm text-muted-foreground">
          <time dateTime={post.published}>{formatTrDate(post.published)}</time>
          <span aria-hidden="true"> · </span>
          {post.readingMinutes} dk okuma
          <span aria-hidden="true"> · </span>
          Güncelleme: <time dateTime={post.updated}>{formatTrDate(post.updated)}</time>
          <span aria-hidden="true"> · </span>
          Kategori:{" "}
          <Link
            to="/tr/blog/kategori/$slug"
            params={{ slug: categorySlug }}
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            {post.category}
          </Link>
        </p>

        <TrServiceLinks className="mt-8" heading="Bu yazıyla ilgili SAP hizmetleri" />

        <div className="mt-10 max-w-3xl space-y-12">
          {ArticleGraphic && <ArticleGraphic />}

          {post.sections.map((section) => (
            <section key={section.heading}>
              <h2 className="text-2xl font-bold md:text-3xl">{section.heading}</h2>
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph.slice(0, 40)} className="mt-4 text-lg leading-relaxed text-muted-foreground">
                  <ArticleRichText text={paragraph} />
                </p>
              ))}
              {section.bullets && (
                <ul className="mt-5 space-y-3">
                  {section.bullets.map((bullet) => (
                    <li key={bullet.slice(0, 40)} className="flex gap-3 text-muted-foreground">
                      <span aria-hidden="true" className="mt-2 size-1.5 shrink-0 rounded-full bg-primary" />
                      <span><ArticleRichText text={bullet} /></span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          <section>
            <h2 className="text-2xl font-bold md:text-3xl">Sık sorulan sorular</h2>
            <dl className="mt-6 space-y-6">
              {post.faq.map(([question, answer]) => (
                <div key={question}>
                  <dt className="font-semibold">{question}</dt>
                  <dd className="mt-2 text-muted-foreground">{answer}</dd>
                </div>
              ))}
            </dl>
          </section>
        </div>

        <div className="mt-14 rounded-xl border border-border bg-muted/40 p-8">
          <h2 className="text-2xl font-bold">Bir sonraki adım</h2>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            Bu konuyu kendi projenizde nasıl uygulayacağınızı birlikte netleştirelim.
          </p>
          <Link
            to={post.cta.to}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-5 py-3 font-semibold text-primary-foreground"
          >
            {post.cta.label}
            <ArrowRight className="size-4" aria-hidden="true" />
          </Link>
        </div>

        <TrServiceLinks
          className="mt-10"
          heading="Devamını okuyun"
          exclude={post.cta.to}
        />

        {others.length > 0 && (
          <section className="mt-16">
            <h2 className="text-2xl font-bold">Diğer yazılar</h2>
            <ul className="mt-6 grid gap-6 md:grid-cols-3">
              {others.map((other) => (
                <li key={other.slug} className="rounded-xl border border-border bg-card p-5">
                  <p className="eyebrow text-primary">— {other.category}</p>
                  <h3 className="mt-2 text-lg font-bold leading-snug">
                    <Link
                      to="/tr/blog/$slug"
                      params={{ slug: other.slug }}
                      className="underline-offset-4 hover:underline"
                    >
                      {other.title}
                    </Link>
                  </h3>
                </li>
              ))}
            </ul>
          </section>
        )}
      </article>
    </>
  );
}
